import { lazy, Suspense, useState } from 'react'

import { AuthModal } from '@/modules/auth/components/AuthModal'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { HomePage } from '@/modules/home/components/HomePage'
import { InstallPrompt } from '@/modules/pwa/components/InstallPrompt'
import { UpdatePrompt } from '@/modules/pwa/components/UpdatePrompt'

// Lazy — każda sekcja w osobnym chunku. Pierwszy load (Home + nav) jest mały;
// reszta jeżdżą gdy user faktycznie kliknie zakładkę.
const CwiczeniaPage  = lazy(() => import('@/modules/cwiczenia/components/CwiczeniaPage').then(m => ({ default: m.CwiczeniaPage })))
const SkojarzeniaPage = lazy(() => import('@/modules/skojarzenia/components/SkojarzeniaPage').then(m => ({ default: m.SkojarzeniaPage })))
const TeoriaPage     = lazy(() => import('@/modules/teoria/components/TeoriaPage').then(m => ({ default: m.TeoriaPage })))
const NauczycielPage = lazy(() => import('@/modules/nauczyciel/components/NauczycielPage').then(m => ({ default: m.NauczycielPage })))
const AdminPage      = lazy(() => import('@/modules/admin/components/AdminPage').then(m => ({ default: m.AdminPage })))

function PageFallback() {
  return (
    <div className="max-w-2xl mx-auto p-6 pt-24 text-center">
      <p className="font-ui text-sm text-ink/70">Ładowanie sekcji…</p>
    </div>
  )
}

type Page = 'home' | 'cwiczenia' | 'skojarzenia' | 'teoria' | 'nauczyciel' | 'admin'

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home')
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const { user, isLoading: isAuthLoading, logout, isTeacher, isAdmin } = useAuth()
  const hasTeacherAccess = isTeacher || isAdmin

  const handleLogout = async () => {
    await logout()
  }

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-paper border-b border-ink/10">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Wordmark */}
            <button
              onClick={() => setCurrentPage('home')}
              className="flex items-baseline gap-1.5 mr-1 group"
              title="Interwały — strona główna"
            >
              <span className="font-display text-ochre leading-none" style={{ fontSize: '1.4rem' }} aria-hidden="true">𝄞</span>
              <span className="font-display text-xl text-ink leading-none tracking-tight group-hover:text-ochre transition-colors">interwały</span>
            </button>
            <span className="w-px h-5 bg-ink/15" aria-hidden="true" />
            <button
              onClick={() => setCurrentPage('cwiczenia')}
              className={`font-ui text-sm transition-colors ${
                currentPage === 'cwiczenia' 
                  ? 'text-ochre font-medium' 
                  : 'text-ink/70 hover:text-ink'
              }`}
            >
              Ćwiczenia
            </button>
            <button
              onClick={() => setCurrentPage('skojarzenia')}
              className={`font-ui text-sm transition-colors ${
                currentPage === 'skojarzenia' 
                  ? 'text-ochre font-medium' 
                  : 'text-ink/70 hover:text-ink'
              }`}
            >
              Skojarzenia
            </button>
            <button
              onClick={() => setCurrentPage('teoria')}
              className={`font-ui text-sm transition-colors ${
                currentPage === 'teoria' 
                  ? 'text-ochre font-medium' 
                  : 'text-ink/70 hover:text-ink'
              }`}
            >
              Teoria
            </button>
            {/* Panel admina — tylko admin */}
            {isAdmin && (
              <button
                onClick={() => setCurrentPage('admin')}
                className={`font-ui text-sm transition-colors ${
                  currentPage === 'admin'
                    ? 'text-burgundy font-medium'
                    : 'text-ink/70 hover:text-ink'
                }`}
              >
                Panel admina
              </button>
            )}
            {/* Panel nauczyciela widoczny dla nauczycieli i adminów */}
            {hasTeacherAccess && (
              <button
                onClick={() => setCurrentPage('nauczyciel')}
                className={`font-ui text-sm transition-colors ${
                  currentPage === 'nauczyciel' 
                    ? 'text-ochre font-medium' 
                    : 'text-ink/70 hover:text-ink'
                }`}
              >
                Panel nauczyciela
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            {/* Auth status */}
            {isAuthLoading ? (
              <span className="font-ui text-xs text-ink/65">Ładowanie...</span>
            ) : user ? (
              <div className="flex items-center gap-3">
                <span className="font-ui text-xs text-ink/70">
                  {user.displayName || user.email}
                  {isAdmin && <span className="text-ochre ml-1">(admin)</span>}
                  {isTeacher && !isAdmin && <span className="text-ochre ml-1">(nauczyciel)</span>}
                </span>
                <button
                  onClick={handleLogout}
                  className="font-ui text-xs text-burgundy hover:underline"
                >
                  Wyloguj
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="font-ui text-xs text-ochre hover:underline"
              >
                Zaloguj / Zarejestruj
              </button>
            )}
            <span className="font-ui text-xs text-ink/65">
              Interwały v0.3
            </span>
          </div>
        </div>
      </nav>

      {/* Page content — Suspense łapie lazy chunki */}
      <div className="pt-12">
        {currentPage === 'home' && (
          <HomePage
            onNavigate={(p) => setCurrentPage(p)}
            onOpenAuth={() => setIsAuthModalOpen(true)}
            isLoggedIn={!!user}
          />
        )}
        <Suspense fallback={<PageFallback />}>
          {currentPage === 'cwiczenia'  && <CwiczeniaPage />}
          {currentPage === 'skojarzenia' && <SkojarzeniaPage />}
          {currentPage === 'teoria'     && <TeoriaPage />}
          {currentPage === 'nauczyciel' && hasTeacherAccess && <NauczycielPage />}
          {currentPage === 'admin'      && isAdmin && <AdminPage />}
        </Suspense>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />

      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* SW update prompt — pojawia się gdy nowa wersja czeka */}
      <UpdatePrompt />
    </div>
  )
}

export default App
